import { useEffect, useState } from 'react';

/* For the #wow-forever crowd: now and then someone from Azeroth wanders along
   the bottom of the order list. Rare on purpose: at most once in a few days per
   device, never tappable, under every sheet, gone by itself. Five taps on the
   mascot call one straight away. The characters are drawn here, pixel by pixel. */
const COLORS = {
  g: '#5f9e3a', G: '#3f7327', k: '#14202b', w: '#f4efe0', b: '#7a4f2a', B: '#54351b', s: '#9aa3ad', S: '#c9d0d8',
  t: '#2f9e94', T: '#1f7069', y: '#f6d13c', r: '#c23b32', p: '#8a4fb0', u: '#2f62c9', h: '#b98a52', l: '#e8c9a0', o: '#d98a2b',
};
const CAST = [
  {
    who: 'peon',
    says: ['Work work.', 'Zug zug.', 'Me not that kind of orc!'],
    body: [
      '.............ss.',
      '............sbss',
      '....gggggg..b...',
      '...gggggggg.b...',
      '...gkggggkgb....',
      '...gggggggb.....',
      '...gwggggwb.....',
      '....gggggbg.....',
      '..BBBBBBBBBBB...',
      '.gBBbbbbbbbBBg..',
      '.gBBbbbbbbbBBg..',
      '.g.BBbbbbbBB.g..',
      '...BBBBBBBBB....',
    ],
    a: ['...GG.....GG....', '...kk.....kk....'],
    b: ['....GG...GG.....', '....kk...kk.....'],
  },
  {
    who: 'murloc',
    says: ['Mrglglglgl!', 'Mrrrgl?', 'Aaaughibbrgurgle!'],
    body: [
      '......T.........',
      '...T..TT..T.....',
      '...TTtttttT.....',
      '..tttttttttt....',
      '..tykttttykt....',
      '..tttttttttt....',
      '..trrrrrrrrt....',
      '..twrwrwrwrt....',
      '...tttttttt.....',
      '.tt.tTTTTt.tt...',
      '.t..tTTTTt..t...',
      '....tTTTTt......',
    ],
    a: ['...tt....tt.....', '..ttt....ttt....'],
    b: ['....tt..tt......', '...ttt..ttt.....'],
  },
  {
    who: 'goblin',
    says: ['Time is money, friend!', 'Somebody call for a tinker?', 'Everything is for sale.'],
    body: [
      '................',
      '.....gggggg.....',
      'gg..gggggggg..gg',
      '.ggggkggggkgggg.',
      '...gggggggggg...',
      '....gggwwggg....',
      '.....gggggg.....',
      '....ooyoooo.....',
      '...gooyoooog....',
      '...gooyoooog....',
      '....ooyoooo.....',
      '....pppppp......',
    ],
    a: ['....gg..gg......', '....kk..kk......'],
    b: ['.....gggg.......', '.....kkkk.......'],
  },
  {
    who: 'tauren',
    says: ['Moo.', 'Walk with the Earth Mother.', 'Yes, I am a cow. Next question.'],
    body: [
      'h..............h',
      'hh....BBBB....hh',
      '.hh.BBBBBBBB.hh.',
      '..hhBBBBBBBBhh..',
      '...BBkBBBBkBB...',
      '...BBBBBBBBBB...',
      '....BBlllbBB....',
      '.....BlkklB.....',
      '..bbbbbbbbbbbb..',
      '.bbbpppppppbbbb.',
      '.bb.pppppppp.bb.',
      '.bb.pppppppp.bb.',
      '....bbbbbbbb....',
    ],
    a: ['...BB.....BB....', '...kk.....kk....'],
    b: ['....BB...BB.....', '....kk...kk.....'],
  },
  {
    who: 'paladin',
    says: ['For the Light!', 'Bubble, hearth, gone.', 'By the Light, is that a backorder?'],
    body: [
      '.....yyyyy......',
      '....SSSSSSS.....',
      '....SSSSSSS.....',
      '....SkllkSS.....',
      '....SlllllS.....',
      '.....lhhll......',
      '..yySSSSSSSyy...',
      '.uSSSSyySSSSS.S.',
      '.uSSSyyyySSSS.S.',
      '.u.SSSyySSS.S.S.',
      '.u.SSSSSSSS...y.',
      '.u.ssssssss.....',
    ],
    a: ['...ss....ss.....', '...uu....uu.....'],
    b: ['....ss..ss......', '....uu..uu......'],
  },
];

const SEEN_KEY = 'inny.wanderer';
const COOLDOWN = 3 * 24 * 3600 * 1000;
const CHANCE = 0.08; // per visit to the list, once the cooldown is over
const pick = (list) => list[Math.floor(Math.random() * list.length)];

function Pixels({ rows, top = 0, className }) {
  return (
    <g className={className}>
      {rows.flatMap((row, y) => [...row].map((c, x) => (COLORS[c] ? <rect key={`${x}-${y}`} x={x} y={y + top} width="1.02" height="1.02" fill={COLORS[c]} /> : null)))}
    </g>
  );
}

function Walker({ onDone }) {
  const [{ c, line, back }] = useState(() => {
    const c = pick(CAST);
    return { c, line: pick(c.says), back: Math.random() < 0.5 };
  });
  const h = c.body.length + 2;
  return (
    <div aria-hidden="true" className={back ? 'wanderer wanderer-back' : 'wanderer'} onAnimationEnd={(e) => e.animationName === (back ? 'wander-back' : 'wander') && onDone()}>
      <span className="wanderer-says">{line}</span>
      <svg viewBox={`0 0 16 ${h}`} width="40" height={(40 * h) / 16} shapeRendering="crispEdges" className={back ? 'wanderer-body -scale-x-100' : 'wanderer-body'}>
        <Pixels rows={c.body} />
        <Pixels rows={c.a} top={c.body.length} className="wanderer-step-a" />
        <Pixels rows={c.b} top={c.body.length} className="wanderer-step-b" />
      </svg>
    </div>
  );
}

/** `summon`: a counter from the mascot's five taps. */
export default function Wanderer({ summon = 0 }) {
  const [walking, setWalking] = useState(0);

  useEffect(() => {
    try {
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
      if (Date.now() - Number(localStorage.getItem(SEEN_KEY) || 0) < COOLDOWN || Math.random() > CHANCE) return undefined;
    } catch {
      return undefined;
    }
    const t = setTimeout(() => {
      try {
        localStorage.setItem(SEEN_KEY, String(Date.now()));
      } catch {
        /* then it may come back sooner, no harm */
      }
      setWalking((n) => n + 1);
    }, 15000 + Math.random() * 60000); // not as the screen opens: when nobody is expecting it
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (summon) setWalking((n) => n + 1);
  }, [summon]);

  return walking ? <Walker key={walking} onDone={() => setWalking(0)} /> : null;
}
